import React, { useEffect, useMemo, useState } from 'react';
import friendlyError from '../../utils/friendlyError';
import styled from 'styled-components';
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';
import { FaHeartbeat, FaThumbsUp, FaCommentDots, FaNewspaper, FaFire, FaArrowUp, FaArrowDown, FaSearch, FaFilter } from 'react-icons/fa';
import {
  StatStrip, StatTile, StatLabel, StatValue, StatSub, Empty, Loading, ErrorBox,
  borderSoft, mono, accent,
} from '../../styles/techComponents';
import { getPulsoSocial, getUrgenciaPrensa } from '../../services/urgenciaService';

const C = { pos: '#22c55e', neg: '#ef4444', neu: '#94a3b8', score: '#00b4d8', ch: '#00b4d8', pa: '#a855f7' };

const ChartsGrid = styled.div` display:grid;grid-template-columns:repeat(auto-fill,minmax(320px,1fr));gap:0.8rem; `;
const Panel = styled.div` border:1px solid ${borderSoft};border-radius:10px;background:rgba(11,19,32,0.4);padding:0.8rem 0.9rem;
  h5{margin:0 0 0.6rem;font-size:0.88rem;display:flex;align-items:center;gap:0.4rem;color:var(--text-primary)}
  h5 svg{color:${accent}} `;
const PostRow = styled.div` border:1px solid ${borderSoft};border-radius:8px;padding:0.5rem 0.6rem;margin-bottom:0.4rem;background:rgba(0,0,0,0.2);
  .cap{font-size:0.84rem;color:var(--text-primary);line-height:1.4;margin-bottom:0.3rem}
  .m{display:flex;gap:0.9rem;font-size:0.74rem;color:var(--text-secondary);font-family:${mono}}
  .m .neg{color:${C.neg}} `;
const Tema = styled.span` display:inline-block;font-size:0.78rem;padding:0.25rem 0.55rem;border:1px solid ${borderSoft};border-radius:999px;
  margin:0 0.35rem 0.35rem 0;color:var(--text-secondary);background:rgba(11,19,32,0.5); `;
const FuenteNota = styled.p` margin:0 0 0.6rem;font-size:0.8rem;color:var(--text-secondary);line-height:1.5; `;
const NotaLog = styled.li` font-size:0.76rem;color:var(--text-secondary);font-family:${mono};line-height:1.6;list-style:none; `;
const FilterBar = styled.div`
  display:flex;align-items:center;gap:0.6rem;flex-wrap:wrap;margin-bottom:0.8rem;padding:0.6rem 0.8rem;
  border:1px solid rgba(0,180,216,0.3);border-radius:9px;background:rgba(0,180,216,0.06);
  .txt{font-size:0.82rem;color:var(--text-secondary);line-height:1.4;flex:1;min-width:220px}
  .kw{display:inline-block;font-size:0.7rem;font-family:${mono};padding:0.1rem 0.4rem;margin:0 0.25rem 0.25rem 0;
    border:1px solid ${borderSoft};border-radius:5px;color:${accent};background:rgba(11,19,32,0.5)}
`;
const Seg = styled.div` display:inline-flex;border:1px solid ${borderSoft};border-radius:7px;overflow:hidden;flex-shrink:0;
  button{background:transparent;border:0;color:var(--text-secondary);font-family:${mono};font-size:0.74rem;padding:0.35rem 0.7rem;cursor:pointer}
  button.on{background:${accent};color:#04121c}
`;
const tipStyle = { background: '#0b1320', border: `1px solid ${borderSoft}`, borderRadius: 8, fontSize: 12, color: '#e5e7eb' };
const fmtDate = (d) => String(d).slice(5, 10);

// Stopwords + entidades genéricas (aparecen en TODO el corpus, no discriminan la situación).
const STOP = new Set([
  'de', 'la', 'el', 'en', 'por', 'las', 'los', 'del', 'y', 'a', 'un', 'una', 'que', 'con', 'para', 'su', 'sus', 'se', 'al',
  'lo', 'le', 'les', 'mi', 'mis', 'tu', 'como', 'sobre', 'entre', 'desde', 'hasta', 'pero', 'mas', 'muy', 'ya', 'no', 'si',
  'o', 'u', 'e', 'este', 'esta', 'estos', 'estas', 'ese', 'esa', 'son', 'fue', 'ser', 'hay', 'han', 'ha', 'nos', 'porque',
  'chouhy', 'passaglia', 'hechos', 'municipio', 'municipal', 'san', 'nicolas', 'arroyos', 'concejo', 'concejal', 'federico',
  'manuel', 'santiago', 'ciudad', 'gobierno', 'gestion', 'intendente', 'intendencia', 'candidato',
]);

const DIACRITICS = new RegExp('[\\u0300-\\u036f]', 'g');
function norm(s) {
  return String(s || '').normalize('NFD').replace(DIACRITICS, '').toLowerCase();
}
// Palabras distintivas de la situación + raíz cruda (para matchear plurales/conjugaciones).
function extractKeywords(s) {
  if (!s) return [];
  const toks = norm(s).split(/[^a-z0-9]+/).filter(Boolean);
  const seen = new Set(); const kws = [];
  for (const t of toks) {
    if (t.length < 4 || STOP.has(t)) continue;
    const root = t.length > 6 ? t.slice(0, Math.max(5, t.length - 2)) : t;
    if (!seen.has(root)) { seen.add(root); kws.push(root); }
  }
  return kws;
}
function matchKw(text, kws) {
  if (!kws.length) return true;
  const n = norm(text);
  return kws.some((k) => n.includes(k));
}
const sumBy = (arr, k) => arr.reduce((a, x) => a + (x[k] || 0), 0);

export default function PulsoSocial({ situacion }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const kws = useMemo(() => extractKeywords(situacion), [situacion]);
  const [modo, setModo] = useState(situacion ? 'situacion' : 'todo');
  const [prensa, setPrensa] = useState(null);
  const [prensaLoading, setPrensaLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setError(null);
      try {
        const res = await getPulsoSocial();
        if (alive) setData(res);
      } catch (e) { if (alive) setError(friendlyError(e)); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  // Cobertura de prensa AMPLIA para la situación (toda la base, no solo el snapshot).
  useEffect(() => {
    if (!situacion) return undefined;
    let alive = true;
    setPrensaLoading(true);
    (async () => {
      try {
        const res = await getUrgenciaPrensa(situacion);
        if (alive) setPrensa(res);
      } catch { if (alive) setPrensa(null); }
      finally { if (alive) setPrensaLoading(false); }
    })();
    return () => { alive = false; };
  }, [situacion]);

  if (loading) return <Loading>Cargando el pulso social…</Loading>;
  if (error) return <ErrorBox>{error}</ErrorBox>;

  const serie = (data?.serie || []).map((s) => ({ ...s, d: fmtDate(s.fecha) }));
  const actual = data?.actual;
  const det = data?.detalle || {};
  const delta = data?.delta || 0;

  if (!serie.length && !actual) {
    return <Empty>Todavía no hay datos de redes/reputación. El snapshot diario (<code>reputation-snapshot</code>, 9:00) captura posts, comentarios y ánimo social; cuando corra, acá vas a ver las gráficas.</Empty>;
  }

  // --- Filtrado por relevancia a la situación ---
  const puedeFiltrar = !!situacion && kws.length > 0;
  const allFuentes = Array.isArray(det.fuentes) ? det.fuentes : [];
  const allSov = Array.isArray(det.sov_fuentes) ? det.sov_fuentes : [];
  const allPrensa = Array.isArray(det.menciones_prensa) ? det.menciones_prensa : [];
  const fuentesRel = puedeFiltrar ? allFuentes.filter((p) => matchKw(p.caption, kws)) : allFuentes;
  const sovRel = puedeFiltrar ? allSov.filter((m) => matchKw(m.title, kws)) : allSov;
  const prensaRel = puedeFiltrar ? allPrensa.filter((m) => matchKw(m.title, kws)) : allPrensa;
  const hayRelevante = fuentesRel.length > 0 || sovRel.length > 0 || prensaRel.length > 0;
  const filtrando = puedeFiltrar && modo === 'situacion' && hayRelevante;

  const fuentesUsar = filtrando ? fuentesRel : allFuentes;
  const sovUsar = filtrando ? sovRel : allSov;
  const prensaUsar = filtrando ? prensaRel : allPrensa;

  // Totales/sentimiento: recalculados sobre lo filtrado o globales.
  const posV = filtrando ? sumBy(fuentesUsar, 'pos') : (actual?.positivas || 0);
  const negV = filtrando ? sumBy(fuentesUsar, 'neg') : (actual?.negativas || 0);
  const neuV = filtrando ? sumBy(fuentesUsar, 'neu') : (actual?.neutras || 0);
  const postsAnalizados = filtrando ? fuentesUsar.length : (det.posts_analizados ?? '—');
  const postsConCom = filtrando ? fuentesUsar.filter((p) => (p.n || 0) > 0).length : (det.posts_con_comentarios ?? 0);
  const comentariosReales = filtrando ? sumBy(fuentesUsar, 'n') : (det.comentarios_reales ?? actual?.menciones ?? '—');
  const sovChR = filtrando ? sovUsar.filter((m) => m.quien === 'Chouhy').length : det.sov_chouhy;
  const sovPaR = filtrando ? sovUsar.filter((m) => m.quien === 'Passaglia').length : det.sov_passaglia;
  const sovPct = filtrando
    ? ((sovChR + sovPaR) > 0 ? Math.round((sovChR / (sovChR + sovPaR)) * 100) : 0)
    : (actual?.sov_pct ?? null);

  const animo = [
    { name: 'Positivas', value: posV, color: C.pos },
    { name: 'Negativas', value: negV, color: C.neg },
    { name: 'Neutras', value: neuV, color: C.neu },
  ];
  const sov = (sovChR != null || sovPaR != null) ? [
    { name: 'Chouhy', value: sovChR || 0, color: C.ch },
    { name: 'Passaglia', value: sovPaR || 0, color: C.pa },
  ] : (sovPct != null ? [
    { name: 'Chouhy', value: sovPct, color: C.ch },
    { name: 'Passaglia', value: 100 - sovPct, color: C.pa },
  ] : []);
  const posts = fuentesUsar.slice(0, 6);
  const temas = Array.isArray(det?.stats?.temas) ? det.stats.temas.slice(0, 10) : [];
  const esFallbackPrensa = det.fuente === 'prensa' || det.fuente === 'prensa_fallback';
  const fuenteLbl = esFallbackPrensa ? 'titulares de prensa (no hubo comentarios de redes)' : det.fuente === 'social' ? 'comentarios reales de redes' : (det.fuente || '—');
  const mencionesPrensa = prensaUsar.slice(0, 12);
  const sovFuentes = sovUsar.slice(0, 10);
  const notas = Array.isArray(det.notes) ? det.notes : [];

  return (
    <div>
      {puedeFiltrar && (
        <FilterBar>
          <FaFilter style={{ color: accent, flexShrink: 0 }} />
          <div className="txt">
            {filtrando ? (
              <>Mostrando solo lo <strong style={{ color: 'var(--text-primary)' }}>relevante a la situación</strong>: {fuentesRel.length} de {allFuentes.length} posts
                {allSov.length ? <> · {sovRel.length} de {allSov.length} notas de prensa</> : null}. Coincidencias por: {kws.slice(0, 8).map((k, i) => <span className="kw" key={i}>{k}</span>)}</>
            ) : hayRelevante ? (
              <>Mostrando <strong style={{ color: 'var(--text-primary)' }}>todo el universo</strong> (no filtrado a la situación).</>
            ) : (
              <>No se encontraron posts ni notas específicos de esta situación en el último snapshot; se muestra todo el universo. La captura diaria puede no haber traído aún contenido puntual del tema.</>
            )}
          </div>
          {hayRelevante && (
            <Seg>
              <button className={modo === 'situacion' ? 'on' : ''} onClick={() => setModo('situacion')}>Situación</button>
              <button className={modo === 'todo' ? 'on' : ''} onClick={() => setModo('todo')}>Todo</button>
            </Seg>
          )}
        </FilterBar>
      )}

      <StatStrip style={{ marginBottom: '0.8rem' }}>
        <StatTile><StatLabel>Índice hoy</StatLabel><StatValue>{actual?.score ?? '—'}<span style={{ fontSize: '0.7rem' }}>/100</span></StatValue>
          <StatSub style={{ color: delta > 0 ? C.pos : delta < 0 ? C.neg : 'var(--text-secondary)' }}>
            {delta > 0 ? <><FaArrowUp /> +{delta}</> : delta < 0 ? <><FaArrowDown /> {delta}</> : 'sin cambio'} vs día previo</StatSub></StatTile>
        <StatTile><StatLabel>Share of voice</StatLabel><StatValue>{sovPct ?? '—'}%</StatValue><StatSub>{filtrando ? 'en esta situación' : 'vs Passaglia'}</StatSub></StatTile>
        <StatTile><StatLabel>Posts {filtrando ? 'relevantes' : 'analizados'}</StatLabel><StatValue>{postsAnalizados}</StatValue><StatSub>{postsConCom} con comentarios</StatSub></StatTile>
        <StatTile><StatLabel>Comentarios reales</StatLabel><StatValue>{comentariosReales}</StatValue><StatSub>clasificados por IA</StatSub></StatTile>
        <StatTile><StatLabel>Fuente</StatLabel><StatValue style={{ fontSize: '0.9rem' }}>{esFallbackPrensa ? 'prensa' : det.fuente === 'social' ? 'redes' : (det.fuente || '—')}</StatValue><StatSub>{fuenteLbl}</StatSub></StatTile>
      </StatStrip>

      <ChartsGrid>
        <Panel style={{ gridColumn: '1 / -1' }}>
          <h5><FaHeartbeat /> Ánimo social en el tiempo (índice 0-100) {filtrando && <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', fontWeight: 400 }}>· serie global (histórico no se filtra)</span>}</h5>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={serie} margin={{ top: 5, right: 10, left: -18, bottom: 0 }}>
              <defs><linearGradient id="gScore" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={C.score} stopOpacity={0.5} /><stop offset="100%" stopColor={C.score} stopOpacity={0} />
              </linearGradient></defs>
              <CartesianGrid strokeDasharray="3 3" stroke={borderSoft} />
              <XAxis dataKey="d" tick={{ fontSize: 11, fill: '#94a3b8' }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#94a3b8' }} />
              <Tooltip contentStyle={tipStyle} />
              <Area type="monotone" dataKey="score" stroke={C.score} strokeWidth={2} fill="url(#gScore)" name="Índice" />
            </AreaChart>
          </ResponsiveContainer>
        </Panel>

        <Panel>
          <h5><FaCommentDots /> Sentimiento por día (comentarios) {filtrando && <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', fontWeight: 400 }}>· global</span>}</h5>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={serie} margin={{ top: 5, right: 10, left: -18, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={borderSoft} />
              <XAxis dataKey="d" tick={{ fontSize: 11, fill: '#94a3b8' }} />
              <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} />
              <Tooltip contentStyle={tipStyle} />
              <Bar dataKey="positivas" stackId="s" fill={C.pos} name="Positivas" />
              <Bar dataKey="negativas" stackId="s" fill={C.neg} name="Negativas" />
              <Bar dataKey="neutras" stackId="s" fill={C.neu} name="Neutras" />
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel>
          <h5><FaHeartbeat /> Ánimo actual {filtrando && <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', fontWeight: 400 }}>· en esta situación</span>}</h5>
          {animo.some((a) => a.value > 0) ? (
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={animo} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={2}>
                  {animo.map((a, i) => <Cell key={i} fill={a.color} />)}
                </Pie>
                <Tooltip contentStyle={tipStyle} /><Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <Empty>{filtrando ? 'Sin comentarios clasificados en los posts de esta situación.' : 'Sin sentimiento clasificado aún.'}</Empty>}
        </Panel>

        {sov.length > 0 && (
          <Panel>
            <h5><FaFire /> Share of voice vs Passaglia {filtrando && <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', fontWeight: 400 }}>· en esta situación</span>}</h5>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={sov} dataKey="value" nameKey="name" innerRadius={45} outerRadius={75} paddingAngle={2}>
                  {sov.map((a, i) => <Cell key={i} fill={a.color} />)}
                </Pie>
                <Tooltip contentStyle={tipStyle} /><Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
            <FuenteNota style={{ margin: '0.4rem 0 0' }}>
              {sovChR != null
                ? <>Notas de prensa {filtrando ? 'de esta situación' : 'de los últimos 30 días'}: <strong>{sovChR} de Chouhy</strong> vs <strong>{sovPaR} de Passaglia</strong>.{sovFuentes.length > 0 && ' Listadas abajo, en "De dónde salen los datos".'}</>
                : 'Proporción de notas de prensa (monitor de competidores) que mencionan a cada uno.'}
            </FuenteNota>
          </Panel>
        )}

        {posts.length > 0 && (
          <Panel style={{ gridColumn: '1 / -1' }}>
            <h5><FaThumbsUp /> Posts {filtrando ? 'de la situación' : 'con más repercusión'} (engagement real)</h5>
            {posts.map((p, i) => (
              <PostRow key={i}>
                <div className="cap">{(p.caption || 'Publicación').slice(0, 160) || 'Publicación'}</div>
                <div className="m">
                  <span><FaThumbsUp /> {p.likes ?? '—'} likes</span>
                  <span><FaCommentDots /> {p.comments_count ?? p.n ?? '—'} coment.</span>
                  {p.neg != null && <span className="neg">{p.neg} negativos</span>}
                  {p.url && <a href={p.url} target="_blank" rel="noreferrer" style={{ color: accent }}>ver ↗</a>}
                </div>
              </PostRow>
            ))}
          </Panel>
        )}

        {!filtrando && temas.length > 0 && (
          <Panel style={{ gridColumn: '1 / -1' }}>
            <h5><FaNewspaper /> Temas que dominan la conversación</h5>
            {temas.map((t, i) => <Tema key={i}>{typeof t === 'string' ? t : (t.tema || t.nombre || JSON.stringify(t))}</Tema>)}
          </Panel>
        )}

        {situacion && (prensaLoading || (prensa && prensa.total > 0)) && (
          <Panel style={{ gridColumn: '1 / -1' }}>
            <h5><FaNewspaper /> Cobertura de prensa sobre la situación (toda la base)</h5>
            {prensaLoading ? (
              <FuenteNota>Buscando en toda la prensa indexada (medios locales + monitor de competidores)…</FuenteNota>
            ) : (
              <>
                <FuenteNota>
                  <strong style={{ color: 'var(--text-primary)' }}>{prensa.total} notas</strong> en <strong style={{ color: 'var(--text-primary)' }}>{prensa.medios} medios</strong> de
                  los últimos 45 días que mencionan la situación — barriendo las {'>'}11.500 notas indexadas, no solo el snapshot del día.
                </FuenteNota>
                {Array.isArray(prensa.por_medio) && prensa.por_medio.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginBottom: '0.7rem' }}>
                    {prensa.por_medio.slice(0, 14).map((m, i) => (
                      <Tema key={i}>{m.medio} <span style={{ color: accent, fontFamily: mono }}>· {m.n}</span></Tema>
                    ))}
                  </div>
                )}
                {prensa.items.slice(0, 24).map((m, i) => (
                  <PostRow key={i}>
                    <div className="cap">{m.title}</div>
                    <div className="m">
                      {m.quien && m.quien !== 'medio' && <span style={{ color: m.quien === 'Chouhy' ? C.ch : C.pa }}>{m.quien}</span>}
                      <span>{m.outlet}</span>
                      {m.fecha && <span>{fmtDate(m.fecha)}</span>}
                      {m.url && <a href={m.url} target="_blank" rel="noreferrer" style={{ color: accent }}>ver ↗</a>}
                    </div>
                  </PostRow>
                ))}
                {prensa.total > 24 && <FuenteNota style={{ margin: '0.4rem 0 0', fontStyle: 'italic' }}>…y {prensa.total - 24} notas más.</FuenteNota>}
              </>
            )}
          </Panel>
        )}

        <Panel style={{ gridColumn: '1 / -1' }}>
          <h5><FaSearch /> De dónde salen los datos</h5>
          {filtrando ? (
            <FuenteNota>
              Filtrado a la situación <strong>«{situacion}»</strong>: de los {allFuentes.length} posts y {allSov.length} notas del último snapshot,
              se muestran solo los que mencionan las palabras clave de la situación ({kws.slice(0, 6).join(', ')}). Los totales y el sentimiento de arriba
              se recalcularon sobre ese subconjunto. Cambiá a «Todo» para ver el universo completo.
            </FuenteNota>
          ) : esFallbackPrensa ? (
            <FuenteNota>
              La medición diaria buscó comentarios en las redes del candidato, pero no encontró ninguno
              (sin acceso a Instagram disponible en este momento). Para no cortar la serie,
              el ánimo se calculó clasificando con IA los <strong>titulares de prensa de los últimos 14 días</strong> que
              mencionan a Chouhy (medios locales + monitor de competidores).
            </FuenteNota>
          ) : (
            <FuenteNota>
              El ánimo se calculó con <strong>comentarios reales</strong> capturados de las publicaciones listadas arriba
              (Instagram y Facebook del candidato + posteos de medios locales que lo mencionan), clasificados por IA.
            </FuenteNota>
          )}
          {mencionesPrensa.length > 0 && (
            <div style={{ marginBottom: notas.length ? '0.6rem' : 0 }}>
              {mencionesPrensa.map((m, i) => (
                <PostRow key={i}>
                  <div className="cap">{m.title}</div>
                  <div className="m">
                    <span>{m.outlet || 'medio local'}</span>
                    {m.fecha && <span>{fmtDate(m.fecha)}</span>}
                    {m.url && <a href={m.url} target="_blank" rel="noreferrer" style={{ color: accent }}>ver ↗</a>}
                  </div>
                </PostRow>
              ))}
            </div>
          )}
          {sovFuentes.length > 0 && (
            <div style={{ marginBottom: notas.length ? '0.6rem' : 0 }}>
              <FuenteNota style={{ fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 0.4rem' }}>Notas de prensa {filtrando ? 'de la situación' : '(share of voice)'}</FuenteNota>
              {sovFuentes.map((m, i) => (
                <PostRow key={i}>
                  <div className="cap">{m.title}</div>
                  <div className="m">
                    <span style={{ color: m.quien === 'Chouhy' ? C.ch : C.pa }}>{m.quien}</span>
                    <span>{m.source || 'prensa'}</span>
                    {m.fecha && <span>{fmtDate(m.fecha)}</span>}
                    {m.url && <a href={m.url} target="_blank" rel="noreferrer" style={{ color: accent }}>ver ↗</a>}
                  </div>
                </PostRow>
              ))}
            </div>
          )}
          {!filtrando && notas.length > 0 && (
            <ul style={{ margin: 0, padding: 0 }}>
              {notas.map((n, i) => <NotaLog key={i}>· {n}</NotaLog>)}
            </ul>
          )}
        </Panel>
      </ChartsGrid>
    </div>
  );
}
